import React, { useState, useCallback } from 'react';
import { Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import { mapPanelDesktopLayoutSx } from '../../../Access/styles/authPanel.styles';
import { layout } from '../../../styles/theme';

const DEFAULT_SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
const PANEL_LEFT_GAP = layout.mapPanelLeftGap;

/** Ancho visual efectivo del panel en desktop (md+). */
export const DESKTOP_PANEL_WIDTH = 520;

/**
 * PanelShell
 * Wrapper responsive para todos los paneles laterales.
 *
 * Desktop (sm+, sin forceMobile): panel lateral; top/height como AuthLayout.
 * Mobile (xs o forceMobile): bottom sheet con 3 estados:
 *   - 'collapsed' → 64px  (solo el handle visible, mapa completamente usable)
 *   - 'peek'      → 42vh  (mitad inferior, mapa visible arriba)
 *   - 'expanded'  → 74vh  (casi pantalla completa)
 *
 * Props:
 *   uiLeftInset   number    Ancho del sidebar rail (default layout.sidebarRailWidth)
 *   bottomInset   number    Reserva inferior (ej. footer branded en share views)
 *   onClose       fn        Callback para cerrar el panel
 *   children      node      Contenido del panel (header + tabs + content)
 *   initialSnap   string    Estado inicial en mobile: 'peek' | 'expanded' | 'collapsed'
 *   hidden        bool      Mantiene el panel montado pero invisible (preserva scroll/estado)
 *   forceMobile   bool      Fuerza bottom sheet (vistas share: alinear con chrome compacto `lg`)
 *   fullWidth     bool      Sin mapa (WebGL): ocupa todo el ancho debajo del header, sin bottom sheet
 *   topInset      number    Con `fullWidth`: alto del chrome de arriba (header + búsqueda mobile)
 *   notice        node      Con `fullWidth`: aviso arriba del contenido (p. ej. `MapUnavailable`)
 */
const PanelShell = ({
  uiLeftInset = DEFAULT_SIDEBAR_RAIL_WIDTH,
  bottomInset = 0,
  onClose,
  children,
  initialSnap = 'peek',
  hidden = false,
  forceMobile = false,
  fullWidth = false,
  topInset = 0,
  notice = null,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = Boolean(forceMobile) || isNarrow;
  const safeBottomInset = Math.max(0, Number(bottomInset) || 0);

  // 3 estados del bottom sheet en mobile
  const [snap, setSnap] = useState(initialSnap);
  
  const content = typeof children === 'function' ? children({ snap }) : children;

  const cycleSnap = useCallback(() => {
    setSnap((prev) => {
      if (prev === 'collapsed') return 'peek';
      if (prev === 'peek') return 'expanded';
      return 'peek'; // expanded → peek (no vuelve a collapsed con tap)
    });
  }, []);

  // Altura según estado
  const snapHeights = {
    collapsed: '64px',
    peek: '42vh',
    expanded: '74vh',
  };

  // top del panel = header + topInset; hay que restar ambos + footer + gap inferior.
  const desktopBottomGap = 16;
  const desktopVerticalReserve = safeBottomInset > 0
    ? layout.mapHeaderHeight + layout.mapPanelTopInset + safeBottomInset + desktopBottomGap
    : null;
  const desktopLayoutSx = desktopVerticalReserve != null
    ? {
        ...mapPanelDesktopLayoutSx,
        height: {
          sm: `min(928px, calc(100dvh - ${desktopVerticalReserve}px))`,
          md: `min(928px, calc(100dvh - ${desktopVerticalReserve}px))`,
        },
        maxHeight: {
          sm: `calc(100dvh - ${desktopVerticalReserve}px)`,
          md: `calc(100dvh - ${desktopVerticalReserve}px)`,
        },
      }
    : mapPanelDesktopLayoutSx;

  // ── Sin mapa: lista / ficha a todo el ancho (desktop, mobile e iframe) ──────
  if (fullWidth) {
    const fullContent = typeof children === 'function' ? children({ snap: undefined }) : children;
    return (
      <Box
        data-testid="panel-shell-full-width"
        sx={{
          position: 'absolute',
          top: Math.max(0, Number(topInset) || 0),
          left: 0,
          right: 0,
          bottom: safeBottomInset,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          p: { xs: 0, sm: 1.5 },
          boxSizing: 'border-box',
          ...(hidden ? { visibility: 'hidden', pointerEvents: 'none' } : {}),
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {notice ? <Box sx={{ flexShrink: 0, px: { xs: 1, sm: 0 }, pt: { xs: 1, sm: 0 } }}>{notice}</Box> : null}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {fullContent}
          </Box>
        </Box>
      </Box>
    );
  }

  // ── Desktop ─────────────────────────────────────────────────
  if (!isMobile) {
    const content = typeof children === 'function' ? children({ snap: undefined }) : children;
    return (
      <Paper
        sx={{
          position: 'absolute',
          left: PANEL_LEFT_GAP + uiLeftInset,
          right: 'auto',
          ...desktopLayoutSx,
          width: { sm: 420, md: DESKTOP_PANEL_WIDTH },
          maxWidth: `${DESKTOP_PANEL_WIDTH}px`,
          bgcolor: 'transparent', // el color lo pone cada panel
          boxShadow: 'none',      // ídem
          borderRadius: 3,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Pasamos transparencia total para que cada panel
          background: 'none',
          ...(hidden ? { visibility: 'hidden', pointerEvents: 'none' } : {}),
        }}
      >
        {content}
      </Paper>
    );
  }

  // ── Mobile: bottom sheet ─────────────────────────────────────────────────────
  return (
    <>
      {/* Overlay semitransparente solo cuando está expandido */}
      {snap === 'expanded' && !hidden && (
        <Box
          onClick={() => setSnap('peek')}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            bgcolor: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(1px)',
          }}
        />
      )}

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: safeBottomInset,
          zIndex: 1095,
          height: snapHeights[snap],
          transition: 'height 0.30s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.30s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px 16px 0 0',
          overflow: 'hidden',
          ...(hidden ? { visibility: 'hidden', pointerEvents: 'none' } : {}),
        }}
      >
        {/* ── Handle ──────────────────────────────────────────────────────── */}
        <Box
          onClick={cycleSnap}
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 1,
            pb: snap === 'collapsed' ? 1 : 0,
            cursor: 'pointer',
            userSelect: 'none',
            // El fondo lo hereda del Paper interno del panel,
            // pero necesitamos algo aquí para que sea clicable
            bgcolor: 'transparent',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 4,

              borderRadius: 99,
              bgcolor: 'rgba(35, 33, 33, 0.22)',
              transition: 'background-color 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.40)' },
            }}
          />

          {/* En collapsed mostramos un hint de texto */}
          {snap === 'collapsed' && (
            <Box
              sx={{
                mt: 0.75,
                fontSize: '11px',
                color: 'rgba(255,255,255,0.45)',
                letterSpacing: '0.02em',
                pointerEvents: 'none',
              }}
            >
              Tocá para ver detalles
            </Box>
          )}
        </Box>

        {/* ── Contenido del panel (oculto cuando collapsed) ─────────────── */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            opacity: snap === 'collapsed' ? 0 : 1,
            pointerEvents: snap === 'collapsed' ? 'none' : 'auto',
            transition: 'opacity 0.20s ease',
          }}
        >
          {content}
        </Box>
      </Box>
    </>
  );
};

/**
 * Ancho horizontal que ocupa el panel lateral en desktop (rail + margen + panel).
 * Debe coincidir con left + width de PanelShell en md+.
 */
export function getDesktopPanelOccupiedWidth(uiLeftInset = DEFAULT_SIDEBAR_RAIL_WIDTH) {
  return uiLeftInset + PANEL_LEFT_GAP + DESKTOP_PANEL_WIDTH;
}

export default PanelShell;